import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { MIN_PASSWORD_LENGTH, SetPasswordError, SetPasswordState, SetPasswordStore } from './set-password.store';

const SET_PASSWORD_ERROR_MESSAGES: Record<SetPasswordError, string> = {
  tooShort: `Hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.`,
  mismatch: 'Hasła nie są takie same.',
  failed: 'Nie udało się ustawić hasła. Spróbuj ponownie.'
};

// Where a one-time link lands (0057): outside the shell and outside sessionGuard, the sign-in page's sibling.
// The token travels in the URL fragment, which the browser never sends to a server
@Component({
  selector: 'set-password',
  templateUrl: './set-password.page.html',
  styleUrl: './set-password.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink]
})
export class SetPasswordPage implements OnInit, OnDestroy {
  readonly state$: Observable<SetPasswordState>;

  readonly minPasswordLength = MIN_PASSWORD_LENGTH;

  constructor(
    private store: SetPasswordStore,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init(this.route.snapshot.fragment ?? '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  errorMessage(error: SetPasswordError): string {
    return SET_PASSWORD_ERROR_MESSAGES[error];
  }

  // "Witaj, Anno." would need declension — the first name as written is the honest greeting
  firstNameOf(displayName: string): string {
    return displayName.trim().split(/\s+/)[0];
  }

  // Enter submits the form natively; the handler owns the rest
  onSubmit(event: Event, password: string, repeated: string): void {
    event.preventDefault();

    if (this.store.state.submitting) {
      return;
    }

    this.store.setPassword(password, repeated).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.store.state.error || this.store.state.linkStatus !== 'live') {
        return;
      }

      // The session store already holds the new session: the root redirect picks the default route from it
      this.router.navigateByUrl('/');
    });
  }
}
